import { Typography, Box, CircularProgress, Stack, Card, CardContent, IconButton, Select, MenuItem, FormControl, Pagination } from "@mui/material";
import useSWR from 'swr';
import { PATH_NAME } from "../../../configs/pathName";
import { generalFetcher } from "../../../helpers/Fetchers/fetchers";
import colorConfigs from "../../../configs/colorConfigs";
import { typo } from "../../../assets/fonts/font";
import { IStoreCreditWallet } from "../../../models/IStoreCreditWallet";
import walletStyles from "../../../styles/ProtectedPage/wallet";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useState } from "react";
import { OdersPageStyles } from "../../../styles/ProtectedPage/orders";

const WalletPage = () => {
  const { data: walletData, error: walletError } = useSWR(PATH_NAME.API_STORE_CREDITS_WALLET, generalFetcher);
  const { data: transactionsData, error: transactionsError } = useSWR(PATH_NAME.API_STORE_CREDITS_TRANSACTIONS, generalFetcher);
  const navigate = useNavigate();
  const [selectedWalletId, setSelectedWalletId] = useState<string>(""); 
  const [showTransactions, setShowTransactions] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const items_per_page = 10;

  useEffect(() => {
    setPage(1);
  }, [selectedWalletId]);

  const walletItems: IStoreCreditWallet[] = walletData?.items || [];
  const totalBalance = walletItems.reduce((sum, item) => sum + Number(item.balance || 0), 0).toFixed(2);

  // set default selected wallet id once data loaded (first wallet)
  useEffect(() => {
    if (walletItems.length > 0 && !selectedWalletId) {
      setSelectedWalletId(String(walletItems[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletItems]);

  if (walletError) return <div>Error loading wallet data</div>;
  if (!walletData) return <CircularProgress />;
  
  const selectedWallet = walletItems.find(item => String(item.id) === String(selectedWalletId));
  const displayCards = selectedWallet ? [selectedWallet] : [];

  // Transactions
  const transactions: any[] = transactionsData?.items || [];
  const filteredTransactions = selectedWalletId
    ? transactions.filter((tx) => String(tx.store_credit_id) === String(selectedWalletId))
    : transactions; // if no selection, optionally show all
  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / items_per_page));
  const paginatedTransactions = filteredTransactions.slice(
    (page - 1) * items_per_page,
    page * items_per_page
  );

  const renderWalletCard = (item: IStoreCreditWallet, index: number) => (
    <Card key={index} sx={walletStyles.card}>
      <CardContent sx={walletStyles.cardContentLeft}>
        <Typography variant="h6" sx={{ ...typo.title, color: colorConfigs.primary }}>
          Card Type: {item.card_type}
        </Typography>
        <Typography variant="subtitle1" sx={{ ...typo.bodyContent, color: colorConfigs.tertiary }}>
          Balance: ${Number(item.balance || 0).toFixed(2)}
        </Typography>
        {item.expiry ? (
          <Typography variant="subtitle1" sx={{ ...typo.bodyContent, color: colorConfigs.tertiary }}>
            Expiry: {item.expiry}
          </Typography>
        ) : (
          <Typography variant="subtitle1" sx={{ ...typo.bodyContent, color: colorConfigs.tertiary }}>
            No expiry date
          </Typography>
        )}
        {item.valid_from && (
          <Typography variant="subtitle1" sx={{ ...typo.bodyContent, color: colorConfigs.tertiary }}>
            Valid From: {item.valid_from}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <IconButton onClick={() => navigate(PATH_NAME.MEMBER, { replace: true })}>
        <ArrowBackIcon />
      </IconButton>

      <Box sx={walletStyles.containerBox}>
        <Card
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: colorConfigs.quaternary,
            borderRadius: '15px',
            borderStyle: 'solid',
            borderWidth: 2,
            mb: 4,
            mt: 4,
            borderColor: colorConfigs.primary,
          }}
        >
          <Typography variant="body1" sx={{ ...typo.bodyContent, color: colorConfigs.tertiary }}>
            Wallet Balance
          </Typography>
          <Typography variant="h2" sx={{ ...typo.headline, color: colorConfigs.primary, fontWeight: 'bold' }}>
            ${totalBalance}
          </Typography>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <FormControl sx={{ minWidth: '100%' }}>
            <Select
              value={selectedWalletId}
              onChange={(e) => setSelectedWalletId(e.target.value as string)}
              size="small"
              sx={{
                ...typo.bodyContent,
                textAlign: 'center',
                '& .MuiSelect-select': {
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: colorConfigs.secondary,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colorConfigs.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colorConfigs.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colorConfigs.primary,
                },
              }}
            >
              {walletItems.map((wallet) => (
                <MenuItem
                  key={wallet.id}
                  sx={{ ...typo.bodyContent, color: colorConfigs.tertiary }}
                  value={String(wallet.id)}
                >
                  {wallet.card_type} (${Number(wallet.balance || 0).toFixed(2)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {displayCards.length > 0 ? (
          <Stack spacing={2}>
            {displayCards.map((item, index) => renderWalletCard(item, index))}
          </Stack>
        ) : (
          <Typography sx={{ ...typo.smallText, color: colorConfigs.tertiary, textAlign: "center", py: 2 }}>
            No card found for the selected wallet.
          </Typography>
        )}

        {walletItems.length === 0 && (
          <Typography sx={{ ...typo.smallText, color: colorConfigs.primary, textAlign: "center" }}>
            No wallet items found.
          </Typography>
        )}

        <Box sx={{ textAlign: "right", mt: 2, mb:2 }}>
          <Typography
            component="span"
            onClick={() => {
              setShowTransactions(!showTransactions);
              if (!showTransactions) setPage(1);
            }}
            sx={{
              ...typo.bodyContent,
              color: colorConfigs.primary,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {showTransactions ? "Hide Wallet History" : "Show Wallet History"}
          </Typography>
        </Box>

        {showTransactions && (
          <Card sx={walletStyles.card}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ ...typo.title, mb: 2, color: colorConfigs.primary }}
              >
                Wallet History
              </Typography>

              {transactionsError && (
                <Typography color="error">Error loading wallet history</Typography>
              )}

              {!transactionsData ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : filteredTransactions.length > 0 ? (
                <Stack spacing={2}>
                  {paginatedTransactions.map((tx: any) => {
                    const isCredit = tx.type === "credit";
                    const amountPrefix = isCredit ? "+" : "-";

                    return (
                      <Box
                        key={tx.id}
                        sx={{
                          ...OdersPageStyles.orderBox,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid",
                          borderColor: colorConfigs.secondary,
                        }}
                      >
                        {/* Date */}
                        <Typography
                          sx={{ ...typo.label, color: colorConfigs.tertiary }}
                        >
                          {new Date(tx.created_at).toLocaleDateString("en-GB", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })}{" "}
                          {new Date(tx.created_at).toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          })}
                        </Typography>

                        {/* Amount */}
                        <Typography
                          sx={{
                            ...typo.title,
                            color: colorConfigs.primary,
                            minWidth: 120,
                            textAlign: "right",
                          }}
                        >
                          {amountPrefix} ${Number(tx.amount || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    );
                  })}

                  {totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        sx={{
                          "& .MuiPaginationItem-root": {
                            color: colorConfigs.tertiary,
                          },
                          "& .Mui-selected": {
                            color: colorConfigs.secondary,
                            backgroundColor: colorConfigs.primary,
                          },
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography sx={{ ...typo.smallText, color: colorConfigs.tertiary }}>
                  No wallet history found.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

      </Box>
    </Box>
  );
};

export default WalletPage;